import { Box, Card, Typography, List, ListItem, ListItemText, IconButton, Tooltip, CircularProgress, Stack, Divider } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";

/** Matches ListTable `TableColumns` header cell ("Service / Area"). */
const lineItemsTableHeaderSx = {
  fontSize: "0.75rem",
  fontWeight: "bold" as const,
  color: "text.secondary",
  letterSpacing: "0.05em",
  lineHeight: 1,
};

export interface InvoiceBilledVisitRow {
  uid: string;
  title: string;
  subtitle: string;
}

export type InvoiceBilledVisitGroup = { jobId?: number; jobLabel: string; rows: InvoiceBilledVisitRow[] };

interface InvoiceBilledVisitsCardProps {
  visits: InvoiceBilledVisitRow[];
  /** When set, visits are shown in JOB / job groups (client multi-job flow). */
  visitGroups?: InvoiceBilledVisitGroup[];
  isLoading?: boolean;
  onRemove: (uid: string) => void;
  /** When true, remove actions are disabled (only one visit left). */
  removeDisabled?: boolean;
}

function VisitListRow({
  visit,
  removeDisabled,
  onRemove,
}: {
  visit: InvoiceBilledVisitRow;
  removeDisabled: boolean;
  onRemove: (uid: string) => void;
}) {
  return (
    <ListItem
      secondaryAction={
        <Tooltip title={removeDisabled ? "At least one visit must stay on the invoice" : "Remove visit from invoice"}>
          <span>
            <IconButton
              edge="end"
              aria-label="Remove visit from invoice"
              size="small"
              disabled={removeDisabled}
              onClick={() => onRemove(visit.uid)}
              sx={{
                color: removeDisabled ? "action.disabled" : "text.secondary",
                "&:hover": {
                  bgcolor: removeDisabled ? undefined : "action.hover",
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      }
      sx={{
        py: 1.5,
        px: 2,
        alignItems: "flex-start",
        bgcolor: "grey.100",
        borderBottom: invoiceEditorInnerBorder,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <ListItemText
        sx={{ m: 0, pr: 4 }}
        primary={
          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.875rem" }}>
            {visit.title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" component="span" sx={{ display: "block", mt: 0.25, color: "text.secondary", fontSize: "0.875rem" }}>
            {visit.subtitle}
          </Typography>
        }
      />
    </ListItem>
  );
}

export function InvoiceBilledVisitsCard({ visits, visitGroups, isLoading = false, onRemove, removeDisabled = false }: InvoiceBilledVisitsCardProps) {
  const useGrouped = Boolean(visitGroups?.length);
  const totalCount = useGrouped ? visitGroups!.reduce((n, g) => n + g.rows.length, 0) : visits.length;

  if (isLoading) {
    return (
      <Card elevation={0} sx={invoiceEditorSectionCardSx}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  const title = useGrouped ? `Selected visits (${totalCount})` : "Visits on this invoice";
  const subtitle = useGrouped
    ? "Visits are grouped by job. Remove any visit that should not be included."
    : "These job visits will be billed together. Remove any visit that should not be included.";

  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderBottom: invoiceEditorInnerBorder,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.6, mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>

      {useGrouped ? (
        <Stack divider={<Divider flexItem sx={{ borderColor: "divider" }} />} spacing={0}>
          {visitGroups!.map((group) => (
            <Box key={group.jobId != null ? `job-${group.jobId}` : group.jobLabel}>
              <Box
                sx={{
                  bgcolor: "grey.50",
                  px: 2,
                  py: 2,
                  borderBottom: invoiceEditorInnerBorder,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography component="span" sx={{ ...lineItemsTableHeaderSx, textTransform: "uppercase" }}>
                  Job
                </Typography>
                <Typography component="span" sx={{ ...lineItemsTableHeaderSx, textTransform: "uppercase" }}>
                  {" / "}
                </Typography>
                <Typography component="span" sx={{ ...lineItemsTableHeaderSx, textTransform: "none" }}>
                  {group.jobLabel}
                </Typography>
              </Box>
              <List disablePadding sx={{ bgcolor: "grey.100" }}>
                {group.rows.map((visit) => (
                  <VisitListRow key={visit.uid} visit={visit} removeDisabled={removeDisabled} onRemove={onRemove} />
                ))}
              </List>
            </Box>
          ))}
        </Stack>
      ) : (
        <List disablePadding sx={{ bgcolor: "grey.100" }}>
          {visits.map((visit) => (
            <VisitListRow key={visit.uid} visit={visit} removeDisabled={removeDisabled} onRemove={onRemove} />
          ))}
        </List>
      )}

      <Box
        aria-hidden
        sx={{
          bgcolor: "background.paper",
          borderTop: invoiceEditorInnerBorder,
          py: { xs: 1.5, sm: 2 },
          px: 2,
        }}
      />
    </Card>
  );
}
